export type CountryOption = {
  name: string;
  code: string;
  dialCode: string;
};

export const COUNTRY_OPTIONS: CountryOption[] = [
  { name: 'India', code: 'IN', dialCode: '+91' },
  { name: 'United States', code: 'US', dialCode: '+1' },
  { name: 'United Kingdom', code: 'GB', dialCode: '+44' },
  { name: 'United Arab Emirates', code: 'AE', dialCode: '+971' },
  { name: 'Singapore', code: 'SG', dialCode: '+65' },
  { name: 'Australia', code: 'AU', dialCode: '+61' },
  { name: 'Canada', code: 'CA', dialCode: '+1' },
  { name: 'Germany', code: 'DE', dialCode: '+49' },
  { name: 'France', code: 'FR', dialCode: '+33' },
  { name: 'Japan', code: 'JP', dialCode: '+81' },
];

export function dialCodeForCountry(countryName: string): string {
  const match = COUNTRY_OPTIONS.find(
    (c) => c.name.toLowerCase() === countryName.trim().toLowerCase(),
  );
  return match?.dialCode ?? '+91';
}

export function sanitizePhoneDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, 10);
}

export function isValidPhoneDigits(value: string, country?: string): boolean {
  if (!value) return false;
  const digits = value.replace(/\D/g, '');

  // 10-digit validation
  if (digits.length === 10) {
    // Must start with 6, 7, 8, or 9
    if (!/^[6-9]\d{9}$/.test(digits)) {
      return false;
    }
    // Reject repeated digits (e.g. 0000000000, 1111111111, ..., 9999999999)
    if (/^(\d)\1{9}$/.test(digits)) {
      return false;
    }
    // Reject sequential numbers
    const sequential = ['1234567890', '9876543210', '0123456789'];
    if (sequential.includes(digits)) {
      return false;
    }
    return true;
  }

  // International support (7 to 15 digits) if country specified and not India
  if (country && country.trim().toLowerCase() !== 'india') {
    if (digits.length >= 7 && digits.length <= 15) {
      if (/^(\d)\1+$/.test(digits)) {
        return false;
      }
      return true;
    }
  }

  return false;
}

export function passwordStrength(value: string): {
  score: number;
  label: string;
  checks: { label: string; ok: boolean }[];
} {
  const checks = [
    { label: 'At least 8 characters', ok: value.length >= 8 },
    { label: 'Uppercase letter', ok: /[A-Z]/.test(value) },
    { label: 'Lowercase letter', ok: /[a-z]/.test(value) },
    { label: 'Number', ok: /\d/.test(value) },
    { label: 'Special character', ok: /[^A-Za-z0-9]/.test(value) },
  ];
  const score = checks.filter((c) => c.ok).length;
  const label = score <= 2 ? 'Weak' : score <= 4 ? 'Fair' : 'Strong';

  return { score, label, checks };
}

export function isStrongPassword(value: string): boolean {
  return passwordStrength(value).score === 5;
}
